import type { WebErrorCode, WebErrorEnvelope } from "@/shared/contracts";

/** 客户端统一错误。页面只按 kind/code 分支，不解析 message 文本。 */
export type ApiErrorKind = "http" | "network" | "timeout" | "capability" | "aborted";

/** 给玩家看的三句（ApiError.playerMessage 与 StateView 共用）：不说“接口”“能力”“接入”这类开发说法。 */
export const PLAYER_ERROR_TEXT = {
  unavailable: "这里暂时还没开放，准备好了会出现在这里。",
  notFound: "没有找到要看的内容：它可能已经不在了，或者这里暂时还没开放。",
  /** 程序自己出的错（TypeError 之类，经 toApiError 包进来）：原文是给开发看的、可能是英文，玩家只看这一句。 */
  failed: "操作暂时没有完成，请重试。",
} as const;

/**
 * 按 HTTP 状态说人话（ERROR-RECOVERY-01：S-086、R13-26、N-09；Q-204d 的 K2、K3）：client.ts 造的错误（没有错误信封：超时、
 * 网关 502/504、别的没信封的回包）和后端的几句套话（SERVER_BOILERPLATE）都换成下面这几句；后端写给玩家的业务原话照用。
 * 只说发生了什么、能怎么办；不写错误码、编号，不说“请求”“接口”这类开发说法。
 */
export const PLAYER_STATUS_TEXT = {
  login: "登录以后就能接着看。",
  forbidden: "这里你现在看不到了，回上一步看看。",
  noRoad: "这里还没有路，先去别处看看。",
  conflict: "情况刚有变化，刷新看看最新的。",
  fixInput: "有一项没填好，看一下再交。",
  slowDown: "点得有点快了，歇一会儿再来。",
  signal: "这会儿没连上，过一会儿再试一次。",
  timeout: "等了好一会儿没有回音，再试一次。",
  failed: "这一步没做成，再试一次。",
} as const;

/** 没有错误信封、或者只有套话时按状态给的那一句。 */
export function playerTextForStatus(status: number | null): string {
  if (status === 401) return PLAYER_STATUS_TEXT.login;
  if (status === 403) return PLAYER_STATUS_TEXT.forbidden;
  if (status === 404 || status === 405) return PLAYER_STATUS_TEXT.noRoad;
  if (status === 409) return PLAYER_STATUS_TEXT.conflict;
  if (status === 422) return PLAYER_STATUS_TEXT.fixInput;
  if (status === 429) return PLAYER_STATUS_TEXT.slowDown;
  if (status !== null && status >= 500) return PLAYER_STATUS_TEXT.signal;
  return PLAYER_STATUS_TEXT.failed;
}

/**
 * 手机号、邮箱注册带来的六个错误码（契约 I108 起有；CONTRACT-SYNC-I108）：不管后端原话写的什么、回的是什么状态，一律说下面这几句，
 * 码名、后端原话都不上页面。那一套页面（AUTH-CONTACT-UI-01）会按场合说得更细；这里只保证别处碰到时也落到人话上。
 */
const PLAYER_CODE_TEXT: Partial<Record<WebErrorCode, string>> = {
  CODE_INVALID: "验证码不对，再看一眼。",
  CODE_EXPIRED: "验证码过期了，重新获取一个。",
  VERIFY_REQUIRED: "这一步要重新验证，再获取一次验证码。",
  REAUTH_REQUIRED: "为了安全，要先再确认一次是你。",
  CONTACT_TAKEN: "这个手机号或邮箱已经绑在另一个账号上了。",
  REGISTRATION_CLOSED: "现在不能在这里注册新账号。",
};

/** 按错误码给的那一句：只认上面六个码；别的码为 null（照旧用后端写给玩家的原话，或按状态说）。playerError 的出错块也按它分。 */
export function playerTextForCode(code: string): string | null {
  return Object.prototype.hasOwnProperty.call(PLAYER_CODE_TEXT, code) ? (PLAYER_CODE_TEXT as Record<string, string>)[code] : null;
}

/** 后端兜底处理器的几句套话（web_platform/errors.py 的 _http_error、_validation_error）：不是写给玩家的，按状态换成人话。
 *  写成正则（整句比对）：只拿来认后端的原话、不上页面；写成字符串的话，源码扫描会把「没有找到这个接口。」当成页面上的字（UI-BASE-01b，W-21）。 */
const SERVER_BOILERPLATE: readonly RegExp[] = [/^请求未能完成。$/, /^请求内容不符合要求。$/, /^没有找到这个接口。$/];
/** 一个汉字都没有的原话（"boom"、"Internal Server Error"、异常名）是给开发看的，和套话一样不上页面（UI-REVIEW110-01：cand-110 模拟 500 时屏幕上出现「boom」）。 */
const DEVELOPER_TEXT = (said: string) => !/\p{Script=Han}/u.test(said);

/** 422 校验没过时，后端在 details.errors 里给出是哪一项（loc 的最后一段）；认得的字段说出名字，认不出的不说（不露字段名）。 */
const FIELD_TEXT: Record<string, string> = {
  name: "名字",
  species: "TA 是什么动物",
  sex: "男孩还是女孩",
  photo: "照片",
  display_name: "称呼",
  username: "用户名",
  password: "密码",
  birthday: "生日",
  birth_date: "生日",
  text: "要说的话",
  message: "要说的话",
};

export function fieldsText(details: Record<string, unknown> | null): string | null {
  const errors = details?.errors;
  if (!Array.isArray(errors)) return null;
  const names: string[] = [];
  for (const item of errors) {
    const loc = item && typeof item === "object" ? (item as { loc?: unknown }).loc : null;
    const last = Array.isArray(loc) && loc.length ? String(loc[loc.length - 1]) : "";
    const name = Object.prototype.hasOwnProperty.call(FIELD_TEXT, last) ? FIELD_TEXT[last] : null;
    if (name && !names.includes(name)) names.push(name);
  }
  if (!names.length) return null;
  return `「${names.join("」「")}」${names.length > 1 ? "这几项" : "这一项"}没填好，看一下再交。`;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly code: WebErrorCode | "NETWORK_ERROR" | "TIMEOUT" | "ABORTED";
  readonly requestId: string | null;
  readonly retryable: boolean;
  readonly details: Record<string, unknown> | null;
  /**
   * 后端没有这条路（路由级 404/405，由 fromEnvelope 判定）：原话是“没有找到这个接口。”，给玩家要换成人话。
   * 业务上的“找不到”后端都会带上 details（resource 或 reason），原话本来就是写给玩家的（“没有找到这张证件。”），照用。
   */
  readonly unknownRoute: boolean;
  /**
   * 不是 ApiError 的异常（代码 bug、TypeError 之类），由 toApiError 包进来的：原文只进控制台和日志，给玩家看 PLAYER_ERROR_TEXT.failed。
   * kind / code 照旧是 network / NETWORK_ERROR（别处按它决定要不要重试，不动）；真正的网络失败由 client.ts 自己造，不带这个标记。
   */
  readonly fromException: boolean;

  constructor(init: {
    kind: ApiErrorKind;
    status?: number | null;
    code: ApiError["code"];
    message: string;
    requestId?: string | null;
    retryable?: boolean;
    details?: Record<string, unknown> | null;
    unknownRoute?: boolean;
    fromException?: boolean;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.kind = init.kind;
    this.status = init.status ?? null;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.retryable = init.retryable ?? false;
    this.details = init.details ?? null;
    this.unknownRoute = init.unknownRoute ?? false;
    this.fromException = init.fromException ?? false;
  }

  get isAuth(): boolean {
    return this.code === "AUTH_REQUIRED" || this.code === "SESSION_EXPIRED";
  }

  get isCapabilityUnavailable(): boolean {
    return this.code === "CAPABILITY_UNAVAILABLE" || this.code === "NOT_CONFIGURED";
  }

  /** 找不到：HTTP 404 或错误码 NOT_FOUND（出错页的标题据此写“没有找到”）。 */
  get isNotFound(): boolean {
    return this.status === 404 || this.code === "NOT_FOUND";
  }

  /**
   * 给玩家看的一句（2026-09-24 巡检 P1）：还没开放、后端没有这条路（原话带“能力”“接口”这类技术词）的换成人话；
   * 后端兜底的套话（“请求未能完成。”“请求内容不符合要求。”）、空话按状态换成 PLAYER_STATUS_TEXT（422 能认出是哪一项就说那一项）；
   * 其余照用原话——后端业务上的出错原话（含“没有找到这张证件。”这类找不到）本来就是写给玩家看的中文，比一句笼统的“没有找到”说得清楚。
   * 页面上凡是直接显示出错原因的地方都用它，不用 message。错误码、requestId 不进这句（用户 10-09 定稿 X-09／U-26：玩家端不显示技术代码、请求编号、错误码，也不做“展开看详情”的折叠区，要查只在运营后台查）。
   */
  get playerMessage(): string {
    if (this.isCapabilityUnavailable) return PLAYER_ERROR_TEXT.unavailable;
    if (this.unknownRoute) return PLAYER_ERROR_TEXT.notFound;
    if (this.fromException) return PLAYER_ERROR_TEXT.failed;
    // 手机号、邮箱注册那六个码（I108）：后端原话不用，说 PLAYER_CODE_TEXT 那一句
    const byCode = playerTextForCode(this.code);
    if (byCode) return byCode;
    const own = this.message.trim();
    if (own && !DEVELOPER_TEXT(own) && !SERVER_BOILERPLATE.some((said) => said.test(own))) return this.message;
    if (this.kind === "timeout") return PLAYER_STATUS_TEXT.timeout;
    if (this.status === 422 || this.code === "VALIDATION_FAILED") return fieldsText(this.details) ?? PLAYER_STATUS_TEXT.fixInput;
    return playerTextForStatus(this.status);
  }

  static capability(capability: string, message = "这里暂时还没开放。"): ApiError {
    return new ApiError({
      kind: "capability",
      code: "CAPABILITY_UNAVAILABLE",
      message,
      details: { capability },
    });
  }

  static fromEnvelope(status: number, envelope: WebErrorEnvelope, headerRequestId: string | null): ApiError {
    const err = envelope.error;
    return new ApiError({
      kind: err.code === "CAPABILITY_UNAVAILABLE" || err.code === "NOT_CONFIGURED" ? "capability" : "http",
      status,
      code: err.code,
      message: err.message,
      requestId: err.request_id ?? headerRequestId,
      retryable: err.retryable,
      details: err.details ?? null,
      // 后端对“找不到某样东西”都带 details（WebAPIError.not_found 带 resource，其余带 reason）；
      // 什么都不带的 404/405 只来自路由级兜底（web_platform/errors.py 的 _http_error：“没有找到这个接口。”）。
      unknownRoute: err.code === "NOT_FOUND" && (status === 404 || status === 405) && (!err.details || Object.keys(err.details).length === 0),
    });
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * 把任意异常收成 ApiError。不是 ApiError 的（代码 bug、TypeError、第三方库抛的）照旧按网络类处理（可重试），
 * 但标上 fromException：原文（可能是英文）只进控制台和日志，玩家只看“操作暂时没有完成，请重试。”（2026-09-24，驾驶分身转）。
 */
export function toApiError(value: unknown): ApiError {
  if (value instanceof ApiError) return value;
  return new ApiError({
    kind: "network",
    code: "NETWORK_ERROR",
    message: value instanceof Error ? value.message : "网络连接失败",
    retryable: true,
    fromException: true,
  });
}
